import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { 
  Bell, 
  ChevronDown,
  User,
  Settings,
  LogOut,
  Menu as MenuIcon,
  X,
  Target,
  FileCheck,
  FileText
} from "lucide-react";
import { clsx } from "clsx";
import { ResumeSwitcher } from "../ResumeSwitcher";
import { ThemeToggleCompact } from "../ui/ThemeToggle";
import { authClient } from "../../lib/auth-client";

const navigation = [
  { name: 'Discover', href: '/discover', icon: Target },
  { name: 'Matches', href: '/matches', icon: Target },
  { name: 'Saved', href: '/saved', icon: FileCheck },
  { name: 'Applications', href: '/applications', icon: FileText },
  { name: 'Resumes', href: '/resumes', icon: FileText },
  { name: 'Strategy', href: '/strategy', icon: Target },
];

export function Topbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [session, setSession] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await authClient.getSession();
        setSession(data);
      } catch (error) {
        console.error('Session check error:', error);
        setSession(null);
      }
    };
    
    checkSession();
  }, []);

  const handleSignOut = async () => {
    try {
      await authClient.signOut();
      setSession(null);
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
      // Force redirect even if signOut fails
      navigate('/login');
    }
  };

  const isCurrentPath = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border">
      <div className="container">
        <div className="flex h-14 items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center space-x-3">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
                N
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-foreground leading-none">Nduka</span>
                <span className="text-muted-foreground text-xs leading-none">Jobs</span>
              </div>
            </Link>
          </div>

          {/* Center: Navigation (desktop) */}
          <nav className="hidden md:flex items-center space-x-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  "flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors border",
                  isCurrentPath(item.href)
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted border-transparent hover:border-border"
                )}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right: Actions & User Menu */}
          <div className="flex items-center space-x-3">
            {/* Resume Switcher */}
            <ResumeSwitcher className="hidden sm:block" />

            {/* Theme Toggle */}
            <ThemeToggleCompact />

            {/* Notifications */}
            <button className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted text-foreground transition-colors">
              <Bell className="h-4 w-4" />
              <span className="sr-only">Notifications</span>
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-[10px] text-destructive-foreground flex items-center justify-center font-medium">
                3
              </span>
            </button>

            {/* User Menu */}
            <Menu as="div" className="relative">
              <MenuButton className="inline-flex h-9 items-center justify-center space-x-2 rounded-lg border border-border bg-background px-3 text-foreground hover:bg-muted transition-colors">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-3 w-3 text-primary" />
                </div>
                {session?.data?.user && (
                  <span className="text-sm font-medium hidden sm:block max-w-20 truncate">
                    {session.data.user.name || session.data.user.email.split('@')[0]}
                  </span>
                )}
                <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
              </MenuButton>

              <MenuItems className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-lg z-50 py-1">
                {session?.data?.user && (
                  <>
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-medium text-foreground">
                        {session.data.user.name || 'User'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {session.data.user.email}
                      </p>
                    </div>
                  </>
                )}
                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="/settings/profile"
                      className={clsx(
                        "flex items-center px-4 py-2 text-sm transition-colors rounded-lg",
                        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <User className="h-4 w-4 mr-3" />
                      Profile
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={clsx(
                        "flex items-center px-4 py-2 text-sm transition-colors rounded-lg",
                        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Settings
                    </Link>
                  )}
                </MenuItem>
                <div className="border-t border-border my-1" />
                <MenuItem>
                  {({ active }) => (
                    <button
                      onClick={handleSignOut}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm transition-colors rounded-lg text-left",
                        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted text-foreground transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <MenuIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4">
            <div className="space-y-2">
              {/* Mobile Navigation */}
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    "flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors border",
                    isCurrentPath(item.href)
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted border-transparent hover:border-border"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="h-4 w-4 mr-3" />
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Resume Switcher & Theme Toggle */}
              <div className="pt-4 border-t border-border mt-4 space-y-3">
                <ResumeSwitcher className="w-full" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Theme</span>
                  <ThemeToggleCompact />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}